<template>
  <!-- Desktop/Vertical Layout (with SidebarCard wrapper) -->
  <SidebarCard v-if="isDesktopLayout && topNotifications.length > 0" :header="$t('tbs_homepage_notifications')">
    <div :class="containerClasses">
      <component
        v-show="!isFetching"
        v-for="notification in topNotifications"
        :is="notification?.component"
        :key="notification?.id"
        :severity="notification?.impact"
        :id="notification?.id"
        :extraParameters="notification?.extraParams"
        :dismissible="notification?.isDismissible"
        :ttl="notification?.ttl"
      />
    </div>
  </SidebarCard>

  <!-- Mobile/Horizontal Layout (no card wrapper, scrollable) -->
  <div v-else-if="topNotifications.length > 0" class="tw:pt-6 tw:-mx-4">
    <div :class="containerClasses">
      <component
        v-show="!isFetching"
        v-for="(notification, index) in topNotifications"
        :is="notification?.component"
        :key="notification?.id"
        :severity="notification?.impact"
        :id="notification?.id"
        :extraParameters="notification?.extraParams"
        :dismissible="notification?.isDismissible"
        :ttl="notification?.ttl"
        :class="getNotificationClasses(index)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, provide, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import * as Sentry from '@sentry/vue'

import NotificationInvoiceOverdue from '@/components/productNotifications/sidebarNotifications/NotificationInvoiceOverdue.vue'
import NotificationCardExpired from '@/components/productNotifications/sidebarNotifications/NotificationCardExpired.vue'
import NotificationCardToExpire from '@/components/productNotifications/sidebarNotifications/NotificationCardToExpire.vue'
import SidebarCard from '@/components/homePage/sidebar/SidebarCard.vue'
import NotificationInvoicePending from '@/components/productNotifications/sidebarNotifications/NotificationInvoicePending.vue'

import { useNotifications } from '@/components/rateConnect/queries'

const props = defineProps({
  layout: {
    type: String,
    default: 'vertical',
    validator: value => ['vertical', 'horizontal'].includes(value),
  },
})

const route = useRoute()
const routeName = computed(() => (typeof route.name === 'string' ? route.name : String(route.name ?? '')))
const productType = computed(() => {
  if (routeName.value.includes('home')) {
    return 'BILLING'
  } else {
    return null
  }
})

const { notificationsData, isFetching } = useNotifications(productType)

const topNotifications = computed(() => {
  if (!Array.isArray(notificationsData.value)) return []
  return notificationsData.value
    .filter(
      notification => notification.type && notificationToComponent[notification.type] && !isDismissed(notification.id)
    )
    .slice(0, 2)
    .map(notification => ({
      ...notification,
      component: notificationToComponent[notification.type],
    }))
})

const notificationToComponent = {
  INVOICE_OVERDUE: NotificationInvoiceOverdue,
  CC_EXPIRED: NotificationCardExpired,
  CC_ABOUT_TO_EXPIRE: NotificationCardToExpire,
  INVOICE_PENDING: NotificationInvoicePending,
}

const isDesktopLayout = computed(() => props.layout === 'vertical')

const containerClasses = computed(() => {
  if (props.layout === 'horizontal') {
    const hasMultiple = topNotifications.value.length > 1

    if (hasMultiple) {
      return 'tw:flex tw:flex-row tw:gap-4 tw:overflow-x-auto tw:scroll-smooth tw:px-4'
    } else {
      return 'tw:flex tw:px-0'
    }
  }
  return 'tw:flex tw:flex-col tw:gap-y-4'
})

const getNotificationClasses = index => {
  if (props.layout === 'horizontal') {
    const hasMultiple = topNotifications.value.length > 1
    const isFirst = index === 0
    const isLast = index === topNotifications.value.length - 1

    if (hasMultiple) {
      let classes = 'tw:w-[85vw] tw:max-w-[380px] tw:min-w-[280px] tw:flex-shrink-0'

      if (isFirst) classes += ' tw:ml-0'
      if (isLast) classes += ' tw:mr-0'

      return classes
    } else {
      // Single: full width with margins
      return 'tw:w-full tw:mx-4'
    }
  }
  return ''
}

// Dismissal logic
const dismissedNotifications = ref({})
const dismissedNotificationsStorageKey = 'dismissedNotifications'

const dismissNotification = (id, ttl = 0) => {
  const daysInMs = ttl * 24 * 60 * 60 * 1000
  const expirationDate = Date.now() + daysInMs

  dismissedNotifications.value[id] = expirationDate
  try {
    localStorage.setItem(dismissedNotificationsStorageKey, JSON.stringify(dismissedNotifications.value))
  } catch (e) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'notifications')
      Sentry.captureException(e)
    })
  }
}
provide('dismissNotification', dismissNotification)

const isDismissed = id => {
  const expirationDate = dismissedNotifications.value[id]
  if (!expirationDate) return false

  const now = Date.now()
  if (now >= expirationDate) {
    delete dismissedNotifications.value[id]
    localStorage.setItem(dismissedNotificationsStorageKey, JSON.stringify(dismissedNotifications.value))
    return false
  }

  return true
}

const loadDismissedNotifications = () => {
  try {
    const stored = localStorage.getItem(dismissedNotificationsStorageKey)
    if (!stored) return

    const parsed = JSON.parse(stored)
    const now = Date.now()
    let isAnyStoredDismissalExpired = false

    Object.entries(parsed).forEach(([id, expirationDate]) => {
      if (now < expirationDate) {
        dismissedNotifications.value[id] = expirationDate
      } else {
        isAnyStoredDismissalExpired = true
      }
    })

    if (isAnyStoredDismissalExpired) {
      localStorage.setItem(dismissedNotificationsStorageKey, JSON.stringify(dismissedNotifications.value))
    }
  } catch (e) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'notifications')
      Sentry.captureException(e)
    })
  }
}

onMounted(() => {
  loadDismissedNotifications()
})
</script>

<style scoped>
/* Smooth scrolling on mobile */
.tw\:overflow-x-auto {
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

/* Scrollbar styling */
.tw\:overflow-x-auto::-webkit-scrollbar {
  height: 6px;
}

.tw\:overflow-x-auto::-webkit-scrollbar-track {
  background: transparent;
}

.tw\:overflow-x-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.tw\:overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
